import { t } from "@lingui/macro";
import { deepSearchAndParseDates, ErrorMessage } from "@reactive-resume/utils";
import _axios from "axios";

import { showUpgradeModalFromErrorPayload } from "@/client/services/pricing/upgrade-modal";

import { toast } from "../hooks/use-toast";
import { translateError } from "../services/errors/translate-error";
import { getClerkToken } from "./clerk-token";

export type ServerError = {
  statusCode: number;
  code?: ErrorMessage | string;
  message: string;
  error: string;
};

export const axios = _axios.create({ baseURL: "/api", withCredentials: true });

// Attach the Clerk session token (Bearer) on every request. Clerk refreshes the short-lived
// token internally via getToken(), so there is no separate refresh flow anymore — a 401 simply
// means "not signed in" and is handled by AuthGuard.
axios.interceptors.request.use(async (config) => {
  const token = await getClerkToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercept responses to transform ISO dates to JS date objects
axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, ["createdAt", "updatedAt"]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    const data = error?.response?.data as
      | (Partial<ServerError> & { upgradeRequired?: boolean })
      | undefined;

    // Handle pricing/feature limit gating: show upgrade modal and do not toast a generic error
    if (data?.upgradeRequired) {
      try {
        showUpgradeModalFromErrorPayload(data);
      } catch (_) {
        // no-op
      }
      return Promise.reject(error);
    }

    const status = error.response?.status as number | undefined;

    // The signed-in user has a stale/cross-instance local identity link. AuthGuard renders a
    // dedicated recovery page for this state, so a generic toast would be duplicate noise.
    if (data?.code === ErrorMessage.AuthIdentityLinkConflict) return Promise.reject(error);

    // 401 means "not signed in" and is handled by AuthGuard, which redirects. Toasting it
    // would fire on every request made while signed out. Cancellations aren't failures either.
    if (status === 401 || _axios.isCancel(error)) return Promise.reject(error);

    // The request never reached the server (offline, DNS, CORS, timeout). There is no
    // response payload to translate, so `translateError` would return null and the failure
    // would be completely invisible to the user.
    const isNetworkError = !error.response;

    // NestJS sends a string for thrown exceptions, but an array of strings for validation
    // failures — flatten both into something displayable.
    const rawMessage = error.response?.data?.message as ErrorMessage | string | string[];
    const serverMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;

    // Fall back to the raw server message, then to a generic one — an untranslated error is
    // far better than silence, which reads as "the button did nothing".
    const description =
      translateError(rawMessage as ErrorMessage) ??
      (isNetworkError
        ? t`Couldn't reach the server. Please check your connection and try again.`
        : typeof serverMessage === "string" && serverMessage.length > 0
          ? serverMessage
          : t`Something went wrong while processing your request. Please try again.`);

    toast({
      variant: "error",
      title: isNetworkError ? t`You appear to be offline.` : t`Oops, the server returned an error.`,
      description,
    });

    return Promise.reject(error);
  },
);
